import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Check, SlidersHorizontal, Zap,
  Sunrise, Sun, Moon, Coffee as CoffeeIcon, Cookie, ChevronRight, X, Loader2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Input } from '../../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = [
  { id: 'lunes', label: 'Lunes', short: 'LUN', color: '#3B82F6', gradient: 'from-blue-500 to-sky-400' },
  { id: 'martes', label: 'Martes', short: 'MAR', color: '#8B5CF6', gradient: 'from-violet-500 to-purple-400' },
  { id: 'miercoles', label: 'Miercoles', short: 'MIE', color: '#10B981', gradient: 'from-emerald-500 to-teal-400' },
  { id: 'jueves', label: 'Jueves', short: 'JUE', color: '#F59E0B', gradient: 'from-amber-500 to-yellow-400' },
  { id: 'viernes', label: 'Viernes', short: 'VIE', color: '#EF4444', gradient: 'from-red-500 to-rose-400' },
  { id: 'sabado', label: 'Sabado', short: 'SAB', color: '#EC4899', gradient: 'from-pink-500 to-rose-400' },
  { id: 'domingo', label: 'Domingo', short: 'DOM', color: '#F97316', gradient: 'from-orange-500 to-amber-400' },
];

const MEALS = [
  { id: 'desayuno', label: 'Desayuno', icon: Sunrise, color: 'amber', tipo: 'Desayuno' },
  { id: 'merienda1', label: 'Merienda 1', icon: Cookie, color: 'rose', tipo: 'Desayuno' },
  { id: 'almuerzo', label: 'Almuerzo', icon: Sun, color: 'emerald', tipo: 'Almuerzo' },
  { id: 'merienda2', label: 'Merienda 2', icon: Cookie, color: 'violet', tipo: 'Desayuno' },
  { id: 'cena', label: 'Cena', icon: Moon, color: 'indigo', tipo: 'Cena' },
];

const MEAL_COLORS = {
  amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', ring: 'ring-amber-400' },
  rose: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', ring: 'ring-rose-400' },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', ring: 'ring-emerald-400' },
  violet: { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', ring: 'ring-violet-400' },
  indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', ring: 'ring-indigo-400' },
};

const resolveImgUrl = (url) => {
  if (!url) return null;
  return url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
};

// ==============================
// Step 1: Modo
// ==============================
const StepMode = ({ onSelect }) => (
  <div className="space-y-4 animate-fade-in" data-testid="step-mode">
    <div className="text-center mb-8">
      <h2 className="text-2xl font-black text-[#1A2E10]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
        Como quieres crear tu dieta?
      </h2>
      <p className="text-sm text-gray-500 mt-2">Elige el metodo que prefieras</p>
    </div>
    <button onClick={() => onSelect('personalizada')}
      className="w-full bg-white rounded-2xl border-2 border-transparent hover:border-[#467322] p-6 text-left shadow-sm hover:shadow-lg transition-all group" data-testid="mode-personalizada">
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-lg shadow-blue-500/20 group-hover:scale-105 transition-transform">
          <SlidersHorizontal size={24} className="text-white" />
        </div>
        <div className="flex-1">
          <p className="font-bold text-lg text-[#1A2E10]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Forma Personalizada</p>
          <p className="text-sm text-gray-500 mt-1">Selecciona dias, comidas y platos a tu gusto</p>
        </div>
        <ChevronRight size={20} className="text-gray-300 group-hover:text-[#467322] transition-colors" />
      </div>
    </button>
    <button disabled
      className="w-full bg-gray-50 rounded-2xl border-2 border-gray-100 p-6 text-left opacity-60 cursor-not-allowed" data-testid="mode-automatizada">
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-gray-400 to-gray-500 flex items-center justify-center">
          <Zap size={24} className="text-white" />
        </div>
        <div className="flex-1">
          <p className="font-bold text-lg text-gray-500" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Forma Automatizada</p>
          <p className="text-sm text-gray-400 mt-1">Proximamente disponible</p>
        </div>
      </div>
    </button>
  </div>
);

// ==============================
// Step 2: Dias
// ==============================
const StepDays = ({ selectedDays, onToggle }) => (
  <div className="space-y-4 animate-fade-in" data-testid="step-days">
    <div className="text-center mb-6">
      <h2 className="text-2xl font-black text-[#1A2E10]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
        Elige los dias de tu dieta
      </h2>
      <p className="text-sm text-gray-500 mt-2">Selecciona los dias que deseas incluir</p>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {DAYS.map(day => {
        const selected = selectedDays.includes(day.id);
        return (
          <button key={day.id} onClick={() => onToggle(day.id)}
            className={`relative rounded-2xl p-4 text-center transition-all duration-200 ${selected ? `bg-gradient-to-br ${day.gradient} shadow-lg scale-[1.02]` : 'bg-white border-2 border-gray-100 hover:border-gray-200 hover:shadow-sm'}`}
            data-testid={`day-${day.id}`}>
            {selected && <div className="absolute top-2 right-2 w-5 h-5 bg-white rounded-full flex items-center justify-center"><Check size={12} className="text-green-600" /></div>}
            <p className={`text-xs font-bold tracking-widest ${selected ? 'text-white/70' : 'text-gray-400'}`}>{day.short}</p>
            <p className={`text-lg font-black mt-1 ${selected ? 'text-white' : 'text-gray-700'}`} style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{day.label}</p>
          </button>
        );
      })}
    </div>
  </div>
);

// ==============================
// Step 3: Comidas
// ==============================
const StepMeals = ({ selectedMeals, onToggle }) => (
  <div className="space-y-4 animate-fade-in" data-testid="step-meals">
    <div className="text-center mb-6">
      <h2 className="text-2xl font-black text-[#1A2E10]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
        Elige que comidas incluir
      </h2>
      <p className="text-sm text-gray-500 mt-2">Selecciona las comidas para cada dia</p>
    </div>
    <div className="space-y-3">
      {MEALS.map(meal => {
        const selected = selectedMeals.includes(meal.id);
        const mc = MEAL_COLORS[meal.color];
        const IconComp = meal.icon;
        return (
          <button key={meal.id} onClick={() => onToggle(meal.id)}
            className={`w-full rounded-2xl p-4 text-left transition-all duration-200 flex items-center gap-4 ${selected ? `${mc.bg} border-2 ${mc.border} shadow-sm` : 'bg-white border-2 border-gray-100 hover:border-gray-200'}`}
            data-testid={`meal-${meal.id}`}>
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${selected ? mc.bg : 'bg-gray-100'}`}>
              <IconComp size={22} className={selected ? mc.text : 'text-gray-400'} />
            </div>
            <p className={`font-bold text-base flex-1 ${selected ? mc.text : 'text-gray-600'}`} style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{meal.label}</p>
            {selected && <div className={`w-6 h-6 rounded-full ${mc.bg} border ${mc.border} flex items-center justify-center`}><Check size={14} className={mc.text} /></div>}
          </button>
        );
      })}
    </div>
  </div>
);

// ==============================
// Food Card (Uber Eats style)
// ==============================
const FoodCard = ({ item, selected, onToggle, small }) => (
  <button onClick={onToggle}
    className={`relative rounded-2xl overflow-hidden text-left transition-all duration-200 ${selected ? 'ring-2 ring-[#467322] shadow-lg scale-[1.01]' : 'bg-white border border-gray-100 hover:shadow-md hover:scale-[1.01]'}`}
    data-testid={`food-${item.id}`}>
    <div className={`${small ? 'h-24' : 'h-32'} bg-gray-100 overflow-hidden`}>
      {item.image_url ? (
        <img src={resolveImgUrl(item.image_url)} alt={item.nombre} className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-gray-300"><CoffeeIcon size={28} /></div>
      )}
    </div>
    {selected && <div className="absolute top-2 right-2 w-6 h-6 bg-[#467322] rounded-full flex items-center justify-center shadow-lg"><Check size={14} className="text-white" /></div>}
    <div className="p-3">
      <p className="font-bold text-sm text-gray-900 leading-tight line-clamp-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{item.nombre}</p>
      {item.ingredientes && <p className="text-[11px] text-gray-400 mt-1 line-clamp-1">{item.ingredientes}</p>}
      {item.calorias !== undefined && <p className="text-[11px] text-gray-400 mt-1">{item.calorias} kcal</p>}
    </div>
  </button>
);

// ==============================
// Step 4: Seleccionar platos por dia (Uber Eats style)
// ==============================
const StepSelectFood = ({ days, meals, platos, bebidas, selections, setSelections }) => {
  const [activeDayIdx, setActiveDayIdx] = useState(0);
  const [activeMealId, setActiveMealId] = useState(meals[0]?.id || 'desayuno');

  const activeDay = days[activeDayIdx];
  const dayInfo = DAYS.find(d => d.id === activeDay);
  const mealDef = MEALS.find(m => m.id === activeMealId);
  const mc = mealDef ? MEAL_COLORS[mealDef.color] : MEAL_COLORS.amber;

  // Filter platos by tipo_comida matching the meal tipo
  const filteredPlatos = mealDef ? platos.filter(p => p.tipo_comida === mealDef.tipo) : [];

  const dayKey = activeDay;
  const mealKey = activeMealId;
  const currentSelPlatos = selections[dayKey]?.[mealKey]?.platos || [];
  const currentSelBebidas = selections[dayKey]?.bebidas || [];

  const togglePlato = (platoId) => {
    setSelections(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      if (!copy[dayKey]) copy[dayKey] = {};
      if (!copy[dayKey][mealKey]) copy[dayKey][mealKey] = { platos: [] };
      const arr = copy[dayKey][mealKey].platos;
      const idx = arr.indexOf(platoId);
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(platoId);
      return copy;
    });
  };

  const toggleBebida = (bebidaId) => {
    setSelections(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      if (!copy[dayKey]) copy[dayKey] = {};
      if (!copy[dayKey].bebidas) copy[dayKey].bebidas = [];
      const arr = copy[dayKey].bebidas;
      const idx = arr.indexOf(bebidaId);
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(bebidaId);
      return copy;
    });
  };

  const [showBebidas, setShowBebidas] = useState(false);

  // Count selections for badge
  const countForDay = (dayId) => {
    const dayData = selections[dayId] || {};
    let c = 0;
    for (const key of Object.keys(dayData)) {
      if (key === 'bebidas') c += (dayData.bebidas || []).length;
      else c += (dayData[key]?.platos || []).length;
    }
    return c;
  };

  return (
    <div className="animate-fade-in" data-testid="step-select-food">
      {/* Day tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-2 scrollbar-hide">
        {days.map((dayId, i) => {
          const d = DAYS.find(x => x.id === dayId);
          const isActive = i === activeDayIdx;
          const count = countForDay(dayId);
          return (
            <button key={dayId} onClick={() => { setActiveDayIdx(i); setShowBebidas(false); }}
              className={`flex-shrink-0 px-4 py-2.5 rounded-xl font-bold text-sm transition-all ${isActive ? `bg-gradient-to-r ${d.gradient} text-white shadow-lg` : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'}`}
              data-testid={`tab-day-${dayId}`}>
              {d.label}
              {count > 0 && <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full ${isActive ? 'bg-white/30' : 'bg-gray-100'}`}>{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Meal tabs + Bebidas tab */}
      <div className="flex gap-1.5 mb-5 overflow-x-auto pb-1 scrollbar-hide">
        {meals.map(mealId => {
          const m = MEALS.find(x => x.id === mealId);
          const mmc = MEAL_COLORS[m.color];
          const isActive = !showBebidas && activeMealId === mealId;
          const IconComp = m.icon;
          const count = (selections[dayKey]?.[mealId]?.platos || []).length;
          return (
            <button key={mealId} onClick={() => { setActiveMealId(mealId); setShowBebidas(false); }}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all ${isActive ? `${mmc.bg} ${mmc.border} border ${mmc.text}` : 'bg-white border border-gray-200 text-gray-500 hover:bg-gray-50'}`}
              data-testid={`tab-meal-${mealId}`}>
              <IconComp size={14} />
              {m.label}
              {count > 0 && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isActive ? mmc.bg : 'bg-gray-100'}`}>{count}</span>}
            </button>
          );
        })}
        <button onClick={() => setShowBebidas(true)}
          className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all ${showBebidas ? 'bg-cyan-50 border border-cyan-200 text-cyan-700' : 'bg-white border border-gray-200 text-gray-500 hover:bg-gray-50'}`}
          data-testid="tab-bebidas">
          <CoffeeIcon size={14} />
          Bebidas
          {currentSelBebidas.length > 0 && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${showBebidas ? 'bg-cyan-100' : 'bg-gray-100'}`}>{currentSelBebidas.length}</span>}
        </button>
      </div>

      {/* Content */}
      {!showBebidas ? (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <p className="text-sm font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {dayInfo?.label} — {mealDef?.label}
            </p>
            <span className="text-xs text-gray-400">({filteredPlatos.length} platos disponibles)</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {filteredPlatos.map(p => (
              <FoodCard key={p.id} item={p} selected={currentSelPlatos.includes(p.id)} onToggle={() => togglePlato(p.id)} />
            ))}
          </div>
          {filteredPlatos.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <p className="font-semibold">No hay platos para "{mealDef?.label}"</p>
            </div>
          )}
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <CoffeeIcon size={16} className="text-cyan-600" />
            <p className="text-sm font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Bebidas para {dayInfo?.label}
            </p>
            <span className="text-xs text-gray-400">({bebidas.length} bebidas disponibles)</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {bebidas.map(b => (
              <FoodCard key={b.id} item={b} selected={currentSelBebidas.includes(b.id)} onToggle={() => toggleBebida(b.id)} small />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ==============================
// Step 5: Nombre y Resumen
// ==============================
const StepName = ({ name, setName, days, meals, selections, platos, bebidas }) => {
  const getNames = (ids, source) => ids.map(id => source.find(s => s.id === id)?.nombre || id);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="step-name">
      <div className="text-center mb-4">
        <h2 className="text-2xl font-black text-[#1A2E10]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          Nombra tu dieta
        </h2>
        <p className="text-sm text-gray-500 mt-2">Dale un nombre a tu plan personalizado</p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
        <Input value={name} onChange={e => setName(e.target.value)} placeholder="Mi Dieta Personalizada..."
          className="text-center text-lg font-bold border-2 border-gray-200 focus:border-[#467322] rounded-xl h-14" data-testid="diet-name-input"
          style={{ fontFamily: 'Barlow Condensed, sans-serif' }} />
      </div>

      {/* Summary */}
      <div className="space-y-3">
        <p className="text-xs font-bold uppercase tracking-wider text-gray-400">Resumen de tu dieta</p>
        {days.map(dayId => {
          const d = DAYS.find(x => x.id === dayId);
          const dayData = selections[dayId] || {};
          const dayBebidas = dayData.bebidas || [];
          return (
            <div key={dayId} className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
              <div className={`bg-gradient-to-r ${d.gradient} px-4 py-2.5`}>
                <p className="text-white font-black text-sm" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{d.label}</p>
              </div>
              <div className="p-3 space-y-2">
                {meals.map(mealId => {
                  const m = MEALS.find(x => x.id === mealId);
                  const mealPlatos = dayData[mealId]?.platos || [];
                  if (mealPlatos.length === 0) return null;
                  return (
                    <div key={mealId} className="flex gap-2">
                      <span className={`text-[11px] font-bold w-20 flex-shrink-0 ${MEAL_COLORS[m.color].text}`}>{m.label}</span>
                      <div className="flex flex-wrap gap-1">
                        {getNames(mealPlatos, platos).map((n, i) => (
                          <span key={`${mealId}-plato-${i}`} className="text-[11px] bg-gray-100 px-2 py-0.5 rounded-md text-gray-700">{n}</span>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {dayBebidas.length > 0 && (
                  <div className="flex gap-2">
                    <span className="text-[11px] font-bold w-20 flex-shrink-0 text-cyan-700">Bebidas</span>
                    <div className="flex flex-wrap gap-1">
                      {getNames(dayBebidas, bebidas).map((n, i) => (
                        <span key={`bebida-${i}`} className="text-[11px] bg-cyan-50 px-2 py-0.5 rounded-md text-cyan-700">{n}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};


// ==============================
// Main Configurator
// ==============================
const DietConfiguratorPage = ({ saveEndpoint, redirectPath, backPath, backLabel }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const basePath = `/${user?.user_type || 'client'}`;
  const actualSaveEndpoint = saveEndpoint || `${API}/diet/custom`;
  const actualRedirect = redirectPath || `${basePath}/diet`;
  const actualBackPath = backPath || `${basePath}/diet`;
  const actualBackLabel = backLabel || 'Mi Dieta';

  const [step, setStep] = useState(0);
  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedMeals, setSelectedMeals] = useState([]);
  const [selections, setSelections] = useState({});
  const [dietName, setDietName] = useState('');
  const [saving, setSaving] = useState(false);

  const [platos, setPlatos] = useState([]);
  const [bebidas, setBebidas] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/diet/catalog/platos`),
      axios.get(`${API}/diet/catalog/bebidas`),
    ]).then(([pRes, bRes]) => {
      setPlatos(pRes.data);
      setBebidas(bRes.data);
    }).catch(() => toast.error('Error cargando catalogo'))
      .finally(() => setLoadingCatalog(false));
  }, []);

  const toggleDay = (id) => setSelectedDays(prev => prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]);
  const toggleMeal = (id) => setSelectedMeals(prev => prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]);

  const canNext = () => {
    if (step === 1) return selectedDays.length > 0;
    if (step === 2) return selectedMeals.length > 0;
    if (step === 3) {
      // At least 1 plato selected total
      let total = 0;
      for (const dayData of Object.values(selections)) {
        for (const [key, val] of Object.entries(dayData)) {
          if (key === 'bebidas') continue;
          total += (val?.platos || []).length;
        }
      }
      return total > 0;
    }
    if (step === 4) return dietName.trim().length > 0;
    return true;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Build days data with full plato/bebida info
      const daysData = {};
      for (const dayId of selectedDays) {
        daysData[dayId] = {};
        for (const mealId of selectedMeals) {
          const platoIds = selections[dayId]?.[mealId]?.platos || [];
          daysData[dayId][mealId] = {
            platos: platoIds.map(id => {
              const p = platos.find(x => x.id === id);
              return p ? {
                id: p.id, nombre: p.nombre, image_url: p.image_url,
                descripcion: p.descripcion, ingredientes: p.ingredientes,
                calorias: p.calorias, tipo_comida: p.tipo_comida,
                etiquetas: p.etiquetas, receta: p.receta,
              } : { id };
            }),
          };
        }
        const bebidaIds = selections[dayId]?.bebidas || [];
        daysData[dayId].bebidas = bebidaIds.map(id => {
          const b = bebidas.find(x => x.id === id);
          return b ? {
            id: b.id, nombre: b.nombre, image_url: b.image_url,
            descripcion: b.descripcion, calorias: b.calorias,
            etiqueta: b.etiqueta,
          } : { id };
        });
      }

      await axios.post(actualSaveEndpoint, {
        name: dietName.trim(),
        selected_days: selectedDays,
        selected_meals: selectedMeals,
        days: daysData,
      });
      toast.success('Dieta guardada exitosamente!');
      navigate(actualRedirect);
    } catch {
      toast.error('Error al guardar la dieta');
    }
    setSaving(false);
  };

  const STEPS = ['Modo', 'Dias', 'Comidas', 'Platos', 'Nombre'];

  if (loadingCatalog) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#467322]" size={32} />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto pb-32" data-testid="diet-configurator-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => step === 0 ? navigate(actualBackPath) : setStep(step - 1)}
          className="p-2 rounded-xl hover:bg-gray-100 transition-colors" data-testid="config-back">
          <ArrowLeft size={20} className="text-gray-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-black text-[#1A2E10]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Configurador de Dieta</h1>
        </div>
      </div>

      {/* Progress bar */}
      <div className="flex items-center gap-1 mb-8">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center flex-1">
            <div className={`h-1.5 rounded-full flex-1 transition-all duration-300 ${i <= step ? 'bg-[#467322]' : 'bg-gray-200'}`} />
          </div>
        ))}
      </div>
      <p className="text-center text-xs text-gray-400 -mt-5 mb-6">Paso {step + 1} de {STEPS.length} — {STEPS[step]}</p>

      {/* Steps */}
      {step === 0 && <StepMode onSelect={(mode) => { if (mode === 'personalizada') setStep(1); }} />}
      {step === 1 && <StepDays selectedDays={selectedDays} onToggle={toggleDay} />}
      {step === 2 && <StepMeals selectedMeals={selectedMeals} onToggle={toggleMeal} />}
      {step === 3 && <StepSelectFood days={selectedDays} meals={selectedMeals} platos={platos} bebidas={bebidas} selections={selections} setSelections={setSelections} />}
      {step === 4 && <StepName name={dietName} setName={setDietName} days={selectedDays} meals={selectedMeals} selections={selections} platos={platos} bebidas={bebidas} />}

      {/* Bottom nav */}
      {step > 0 && (
        <div className="fixed bottom-20 left-0 right-0 bg-white/90 backdrop-blur-xl border-t border-gray-100 p-4 z-30">
          <div className="max-w-5xl mx-auto flex gap-3">
            <button onClick={() => setStep(step - 1)}
              className="px-6 py-3 rounded-xl border border-gray-200 text-gray-600 font-bold text-sm hover:bg-gray-50 transition-colors" data-testid="config-prev">
              <ArrowLeft size={16} className="inline mr-1" /> Atras
            </button>
            {step < 4 ? (
              <button onClick={() => setStep(step + 1)} disabled={!canNext()}
                className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all ${canNext() ? 'bg-[#467322] text-white hover:bg-[#3A601C] shadow-lg shadow-[#467322]/20' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                data-testid="config-next">
                Siguiente <ArrowRight size={16} className="inline ml-1" />
              </button>
            ) : (
              <button onClick={handleSave} disabled={!canNext() || saving}
                className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all ${canNext() && !saving ? 'bg-[#467322] text-white hover:bg-[#3A601C] shadow-lg shadow-[#467322]/20' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                data-testid="config-save">
                {saving ? <Loader2 size={16} className="inline animate-spin mr-1" /> : <Check size={16} className="inline mr-1" />}
                Guardar Dieta
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DietConfiguratorPage;
